// utils/reportHydration.ts
import type { SerializedNodes } from '@craftjs/core';

type AnyObj = Record<string, any>;

/**
 * targetReport.serializedData is a JSON string of an array like:
 * [
 *   {"InputField_ijFYqa1sAW": {"label":"License Plate","value":"65EMK"}},
 *   {"CheckboxField_ZiOqt9t9ZZ":{"label":"Occupied","value":"false"}},
 *   ...
 * ]
 *
 * We convert it into an index keyed by:
 *  - preferred: linkId (if present inside the payload subobject as 'linkId' or 'fieldId')
 *  - else: fieldId like "InputField_ijFYqa1sAW"
 *  - else: label (dangerous but works as a last resort)
 */
export function buildReportDataIndex(serializedDataStr: string) {
  let arr: AnyObj[] = [];
  try {
    arr = JSON.parse(serializedDataStr || '[]');
  } catch (e) {
    console.warn('Bad serializedData JSON', e);
    arr = [];
  }

  const byFieldId: Record<
    string,
    { label?: string; value: any; linkId?: string; fieldId?: string }
  > = {};
  const byLabel: Record<string, { value: any }> = {};
  const byLinkId: Record<string, { value: any }> = {};

  for (const entry of arr) {
    const [fieldId, payload] = Object.entries(entry)[0] as [string, AnyObj];
    if (!fieldId || !payload) continue;

    const { label, value } = payload;
    const linkId = payload.linkId || payload.fieldId || undefined;

    // coerce booleans and numbers that were stringified
    const coerced = coerceValue(value);

    if (linkId) byLinkId[String(linkId)] = { value: coerced };

    byFieldId[fieldId] = { label, value: coerced, linkId, fieldId };
    if (label) byLabel[String(label)] = { value: coerced };
  }

  return { byFieldId, byLabel, byLinkId };
}

function coerceValue(v: any) {
  if (typeof v !== 'string') return v;
  const lower = v.toLowerCase().trim();
  if (lower === 'true') return true;
  if (lower === 'false') return false;
  if (!Number.isNaN(Number(v)) && v !== '' && /^[+-]?\d+(\.\d+)?$/.test(v))
    return Number(v);
  return v;
}

// utils/reportHydration.ts (continued)
const LAYOUT_KEYS = new Set(['left', 'top', 'width', 'height', 'angle']);

export function hydrateOutputLayoutWithReport(
  outputLayoutJson: string,
  dataIndex: ReturnType<typeof buildReportDataIndex>,
  opts?: {
    // if provided, only these props will be copied (e.g., ['value','label'])
    propAllowlist?: string[];
    // whether to also copy submitted labels (default true)
    includeLabels?: boolean;
  }
): string {
  const nodes: SerializedNodes = safeParseNodes(outputLayoutJson);
  const { byFieldId, byLabel, byLinkId } = dataIndex;
  //eslint-disable-next-line
  for (const [id, node] of Object.entries(nodes)) {
    const props = (node as any).props || {};
    let matched: { value: any } | null = null;

    // 1) Prefer linkId
    const linkId = props.linkId || props.fieldId;
    if (linkId && byLinkId[String(linkId)]) matched = byLinkId[String(linkId)];

    // 2) else match by props.fieldId if present
    if (!matched && props.fieldId && byFieldId[props.fieldId]) {
      matched = { value: byFieldId[props.fieldId].value };
    }

    // 3) else match by label (fallback)
    if (!matched && props.label && byLabel[String(props.label)]) {
      matched = byLabel[String(props.label)];
    }

    if (matched) {
      const allow = new Set(opts?.propAllowlist || []);
      const copyAll = !opts?.propAllowlist;

      // We copy 'value' (and 'label' if desired) but never layout keys
      if (copyAll || allow.has('value')) props.value = matched.value;

      if (opts?.includeLabels ?? true) {
        // If we have a more authoritative label from payload, copy it too
        // Find it via fieldId table when possible:
        const fromField = props.fieldId ? byFieldId[props.fieldId] : undefined;
        const newLabel = fromField?.label;
        if (newLabel && (copyAll || allow.has('label'))) props.label = newLabel;
      }

      // NEVER overwrite layout props
      for (const k of Object.keys(props)) {
        if (LAYOUT_KEYS.has(k)) continue;
      }

      (node as any).props = props;
    }
  }

  return JSON.stringify(nodes);
}

function safeParseNodes(json: string): SerializedNodes {
  try {
    return JSON.parse(json || '{}');
  } catch (e) {
    console.warn('Bad outputLayout JSON', e);
    return {} as any;
  }
}
